import { useState } from "react";
import { FileDown, PanelLeftClose, PanelLeftOpen, PlayCircle, Presentation, SlidersHorizontal } from "lucide-react";
import { VideoModal } from "./VideoModal";
import { SECTIONS, useDeck } from "../../lib/deck-context";

export const Sidebar = ({ onPresent, onExport }) => {
  const { idx, go, setPanelOpen } = useDeck();
  const [collapsed, setCollapsed] = useState(false);
  const [video, setVideo] = useState(false);
  const c = collapsed;
  return (
    <aside
      data-testid="sidebar-nav"
      data-collapsed={c}
      className={`hidden lg:flex shrink-0 flex-col bg-ink-deep text-white h-screen sticky top-0 transition-[width] duration-300 ease-out overflow-hidden ${c ? "w-[76px]" : "w-[288px]"}`}
    >
      <div className={`border-b border-white/10 ${c ? "px-0 pt-7 pb-6 flex flex-col items-center gap-5" : "px-8 pt-9 pb-8 relative"}`}>
        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed(!c)}
          title={c ? "Expand menu" : "Collapse menu"}
          className={`h-8 w-8 grid place-items-center rounded-full text-white/55 hover:text-white hover:bg-white/10 transition-colors ${c ? "" : "absolute right-5 top-7"}`}
        >
          {c ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
        {c ? (
          <div className="font-mono text-[13px] tracking-[0.1em] text-arq-mid">AQ</div>
        ) : (
          <>
            <div className="font-mono text-[11px] tracking-[0.42em] text-arq-mid">ARQITEK</div>
            <div className="mt-4 font-display text-[26px] leading-[1.1] whitespace-nowrap">Murrieta Family<br />Commons</div>
            <div className="mt-2 text-xs text-white/50 whitespace-nowrap">Joint Venture Partner Presentation</div>
          </>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-5 nav-scroll">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            data-testid={`nav-item-${s.id}`}
            onClick={() => go(i)}
            title={c ? `${s.num} ${s.label}` : undefined}
            className={`group relative w-full flex items-center gap-4 py-[9px] text-left transition-colors duration-200 ${c ? "justify-center px-0" : "px-8"} ${
              i === idx ? "text-white" : "text-white/50 hover:text-white/85"
            }`}
          >
            <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-arq-mid transition-opacity duration-300 ${i === idx ? "opacity-100" : "opacity-0"}`} />
            <span className={`font-mono ${c ? "text-[12px]" : "text-[11px]"} ${i === idx ? "text-arq-mid" : "text-white/35"}`}>{s.num}</span>
            {!c && <span className="text-[14px] whitespace-nowrap">{s.label}</span>}
          </button>
        ))}
        <div className={`mt-4 pt-4 border-t border-white/10 ${c ? "mx-3" : "mx-8"}`}>
          <button
            data-testid="nav-play-video"
            onClick={() => setVideo(true)}
            title={c ? "Play Commons Video" : undefined}
            className={`group w-full flex items-center gap-3 py-2 text-left text-white/70 hover:text-white transition-colors ${c ? "justify-center" : ""}`}
          >
            <PlayCircle size={18} className="text-arq-mid transition-transform group-hover:scale-110" />
            {!c && <span className="text-[14px] whitespace-nowrap">Play Commons Video</span>}
          </button>
          <button
            data-testid="nav-export"
            onClick={onExport}
            title={c ? "PDF / PowerPoint" : undefined}
            className={`group w-full flex items-center gap-3 py-2 text-left text-white/70 hover:text-white transition-colors ${c ? "justify-center" : ""}`}
          >
            <FileDown size={18} className="text-arq-mid transition-transform group-hover:scale-110" />
            {!c && <span className="text-[14px] whitespace-nowrap">PDF / PowerPoint</span>}
          </button>
        </div>
      </nav>
      <VideoModal open={video} onOpenChange={setVideo} />
      <div className={`border-t border-white/10 space-y-3 ${c ? "px-3 py-5" : "px-8 py-6"}`}>
        {!c && (
          <div className="flex justify-between font-mono text-[10px] tracking-[0.2em] text-white/40">
            <span>PROGRESS</span>
            <span data-testid="sidebar-progress-label">{String(idx + 1).padStart(2, "0")} / {SECTIONS.length}</span>
          </div>
        )}
        <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-arq-mid transition-[width] duration-500" style={{ width: `${((idx + 1) / SECTIONS.length) * 100}%` }} />
        </div>
        <button data-testid="sidebar-assumptions-btn" title="Assumptions" onClick={() => setPanelOpen(true)} className="mt-3 w-full flex items-center justify-center gap-2 rounded-full border border-white/20 py-2.5 text-sm text-white/85 hover:bg-white/5 transition-colors">
          <SlidersHorizontal size={15} /> {!c && "Assumptions"}
        </button>
        <button data-testid="sidebar-present-btn" title="Presentation Mode" onClick={onPresent} className="w-full flex items-center justify-center gap-2 rounded-full bg-arq py-2.5 text-sm text-white hover:bg-arq-dark transition-colors">
          <Presentation size={15} /> {!c && "Presentation Mode"}
        </button>
        {!c && <div className="pt-2 text-[10px] font-mono tracking-[0.1em] text-white/35 whitespace-nowrap">PRELIMINARY · CONFIDENTIAL · OCT 2026</div>}
      </div>
    </aside>
  );
};
